import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, SectionEyebrow, ArrowIcon } from "../common/Section";
import { useI18n } from "../../lib/i18n";
import { resolveImage } from "../admin/ImageUpload";

const FeaturedProjects = ({ projects }) => {
  const { pick, t } = useI18n();
  if (!projects?.length) return null;

  // Editorial asymmetric layout: 1 large + rest smaller
  const [primary, ...rest] = projects.slice(0, 5);

  return (
    <section className="bg-white" data-testid="featured-projects-section">
      <div className="container-x py-6 md:py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <Reveal>
            <SectionEyebrow>{t("common.projects")}</SectionEyebrow>
            <h2 className="display display-lg mt-4 max-w-2xl">
              {t("home.featuredProjects")}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/projects" className="link-gold" data-testid="projects-view-all">
              <span>{t("common.viewAll")}</span>
              <ArrowIcon />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
          {/* Primary */}
          {primary ? (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="lg:col-span-8"
              data-testid={`featured-project-${primary.slug}`}
            >
              <Link to={`/projects/${primary.slug}`} className="zoom-parent block group">
                <div className="relative overflow-hidden aspect-[4/3]">
                  <img
                    src={resolveImage(primary.cover_image)}
                    alt={pick(primary.name, "")}
                    className="w-full h-full object-cover zoom-img"
                    loading="lazy"
                  />
                </div>
                <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                  <div>
                    <div className="text-[0.66rem] tracking-[0.28em] uppercase text-brand-medium">
                      {pick(primary.location, "")} · {t(`common.${primary.category}`) || primary.category}
                    </div>
                    <div className="mt-3 font-serif text-3xl md:text-5xl text-brand-navy leading-tight">
                      {pick(primary.name, "")}
                    </div>
                  </div>
                  <div className="link-gold">
                    <span>{t("common.viewProject")}</span>
                    <ArrowIcon />
                  </div>
                </div>
              </Link>
            </motion.div>
          ) : null}

          {/* Rest */}
          <div className="lg:col-span-4 grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-6">
            {rest.map((p, idx) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.1 + idx * 0.1, ease: [0.19, 1, 0.22, 1] }}
                data-testid={`featured-project-${p.slug}`}
              >
                <Link to={`/projects/${p.slug}`} className="zoom-parent block group">
                  <div className="relative overflow-hidden aspect-[5/4]">
                    <img
                      src={resolveImage(p.cover_image)}
                      alt={pick(p.name, "")}
                      className="w-full h-full object-cover zoom-img"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-4">
                    <div className="text-[0.64rem] tracking-[0.28em] uppercase text-brand-medium">
                      {pick(p.location, "")}
                    </div>
                    <div className="mt-2 font-serif text-xl md:text-2xl text-brand-navy leading-tight">
                      {pick(p.name, "")}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
